import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Badge, Col, OverlayTrigger, Row, Spinner, Tooltip} from "react-bootstrap";
import {EQHDatePicker} from "../../../../components/datePicker";
import {format} from "date-fns";
import Modals from "../../../../components/modals";
import axios from "../../../../utils/apiClient";
import {formatDate, formatDueDate} from "../../../../utils/util";
import {getErrorCode, TRACK_EVENTS, trackPendoEvent} from "../../../../utils/pendoTrack";

const componentName = "scheduledAppointment";

const statusDisplayLabels = {
  APPOINTMENT_SCHEDULED: "Appointment Scheduled",
  APPOINTMENT_UNSCHEDULED: "No Show",
  APPOINTMENT_COMPLETED: "Appointment Completed",
  APPOINTMENT_CONFIRMED: "Appointment Confirmed",
  APPOINTMENT_CANCELLED: "Appointment Cancelled",
  LEFT_MESSAGE: "Left Message",
  CHANGED_PCPS: "Changed PCP",
  UNABLE_TO_CONTACT: "Unable to Contact",
  PATIENT_REFUSAL: "Patient Refused",
};

const getStatusDisplayLabel = (statusEnum) => {
  if (!statusEnum) return null;
  return statusDisplayLabels[statusEnum] || statusEnum;
};

const buildVisitGapsUrl = (patientId, action, careGapId) => {
  const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
  const params = [
    `patientId=${encodeURIComponent(patientId ?? "")}`,
    `action=${encodeURIComponent(action ?? "")}`,
  ];

  if (careGapId !== undefined) {
    params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
  }

  return `${baseUrl}/v1/patients/getVisitGaps?${params.join("&")}`;
};

const buildUpdateVisitGapsUrl = (patientId, action, careGapId) => {
  const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
  const params = [
    `patientId=${encodeURIComponent(patientId ?? "")}`,
    `action=${encodeURIComponent(action ?? "")}`,
  ];

  if (careGapId !== undefined) {
    params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
  }

  return `${baseUrl}/v1/patients/updateVisitGaps?${params.join("&")}`;
};

const resolveCareGapId = (patient) => {
  if (!patient) return null;

  const addCandidate = (collection, value) => {
    if (value === undefined || value === null) return;
    const normalized = typeof value === "string" ? value.trim() : value;
    if (normalized !== "" && normalized !== null) {
      collection.push(normalized);
    }
  };

  const candidates = [];

  addCandidate(candidates, patient.nextVisitId);
  addCandidate(candidates, patient.nextVisitID);
  addCandidate(candidates, patient.nextVisitGapId);
  addCandidate(candidates, patient.nextVisitGapID);

  addCandidate(candidates, patient.careGapId);
  addCandidate(candidates, patient.careGapID);
  addCandidate(candidates, patient.patientGapId);
  addCandidate(candidates, patient.patientGapID);

  if (Array.isArray(patient.careGapIds) && patient.careGapIds.length) {
    addCandidate(candidates, patient.careGapIds[0]);
  } else if (typeof patient.careGapIds === "string" && patient.careGapIds.length) {
    addCandidate(candidates, patient.careGapIds.split(",")[0]);
  }

  if (Array.isArray(patient.patientGapIds) && patient.patientGapIds.length) {
    addCandidate(candidates, patient.patientGapIds[0]);
  } else if (typeof patient.patientGapIds === "string" && patient.patientGapIds.length) {
    addCandidate(candidates, patient.patientGapIds.split(",")[0]);
  }

  return candidates.find((id) => id !== undefined && id !== null && id !== "") || null;
};

const resolveVisitAppointmentDate = (visit) => {
  if (!visit) return null;
  return (
    visit.appointmentDate ||
    visit.patientGapAppointmentDate ||
    visit.visitDate ||
    visit.appointmentDateTime ||
    null
  );
};

const resolveVisitStatusDate = (visit) => {
  if (!visit?.statusEnum || visit.statusEnum === "OPEN") return null;

  if (visit.statusEnum === "APPOINTMENT_SCHEDULED") {
    return visit.statusDate || resolveVisitAppointmentDate(visit) || null;
  }

  if (visit.statusEnum === "APPOINTMENT_UNSCHEDULED") {
    if (visit.appointmentCancelReason !== "NO_SHOW") {
      return null;
    }
    return visit.appointmentCancelDate || visit.statusDate || null;
  }

  return visit.statusDate || visit.appointmentDate || visit.appointmentCancelDate || null;
};

const getStatusBadgeText = (visit) => {
  if (!visit) return null;
  const statusEnum = visit.statusEnum;
  if (!statusEnum || statusEnum === "OPEN") return null;

  if (statusEnum === "APPOINTMENT_UNSCHEDULED" && visit?.appointmentCancelReason !== "NO_SHOW") {
    return null;
  }

  if (statusEnum === "APPOINTMENT_SCHEDULED") {
    return null;
  }

  const label = getStatusDisplayLabel(statusEnum);
  const dateValue = resolveVisitStatusDate(visit);
  if (!dateValue) return label;

  const formattedDate = formatDate(dateValue);
  return formattedDate ? `${label} on ${formattedDate}` : label;
};

const normalizeDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length >= 10) {
      return trimmed.slice(0, 10);
    }
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "yyyy-MM-dd");
};

const actionConfig = {
  schedule: {
    title: "Schedule Appointment",
    description: "Select one or more visits and choose the date you want to schedule.",
    submitLabel: "Schedule",
    statusEnum: "APPOINTMENT_SCHEDULED",
    requestAction: "SCHEDULE_APPOINTMENT",
    requiresDate: true,
  },
  reschedule: {
    title: "Reschedule Appointment",
    description: "Select one or more visits and choose the new date.",
    submitLabel: "Save",
    statusEnum: "APPOINTMENT_SCHEDULED",
    requestAction: "RESCHEDULE_APPOINTMENT",
    requiresDate: true,
  },
  unschedule: {
    title: "Unschedule Appointment",
    description: "Select the visits you want to unschedule.",
    submitLabel: "Unschedule",
    statusEnum: "APPOINTMENT_UNSCHEDULED",
    requestAction: "UNSCHEDULE_APPOINTMENT",
    requiresDate: false,
  },
};

export const ScheduledAppointment = ({
  show,
  onHide,
  patient,
  appointmentAction,
  onSuccess,
}) => {
  const normalizedAction = actionConfig[appointmentAction] ? appointmentAction : "schedule";
  const config = actionConfig[normalizedAction];
  const patientId = patient?.id;

  const primaryCareGapId = useMemo(() => resolveCareGapId(patient), [patient]);
  const nextVisitCareGapId = useMemo(() => {
    if (!patient) return null;
    const candidates = [
      patient.nextVisitId,
      patient.nextVisitID,
      patient.nextVisitGapId,
      patient.nextVisitGapID,
    ];
    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) continue;
      const normalized = typeof candidate === "string" ? candidate.trim() : candidate;
      if (normalized !== "" && normalized !== null) {
        return normalized;
      }
    }
    return null;
  }, [patient]);

  const [visitGroups, setVisitGroups] = useState({ dueNowVisits: [], dueSoonVisits: [] });
  const [visitSelections, setVisitSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resetState = useCallback(() => {
    setVisitGroups({ dueNowVisits: [], dueSoonVisits: [] });
    setVisitSelections({});
    setLoading(false);
    setLoadError(null);
    setFormError(null);
    setSubmitError(null);
    setSubmitting(false);
  }, []);

  const buildSelectionMap = useCallback(
    (visits, seed = {}) => {
      if (!Array.isArray(visits)) return seed;
      const next = { ...seed };
      const normalizedPrimary =
        primaryCareGapId === undefined || primaryCareGapId === null
          ? null
          : String(primaryCareGapId).trim();
      const normalizedNextVisit =
        nextVisitCareGapId === undefined || nextVisitCareGapId === null
          ? null
          : String(nextVisitCareGapId).trim();
      for (const visit of visits) {
        const careGapId = visit?.careGapId;
        if (!careGapId) continue;

        const initialDate = normalizeDateInput(resolveVisitAppointmentDate(visit));
        const visitCareGapId =
          careGapId === undefined || careGapId === null ? null : String(careGapId).trim();
        const shouldSelectByDefault =
          !!visit.selected ||
          (normalizedAction !== "schedule" &&
            normalizedPrimary &&
            visitCareGapId === normalizedPrimary) ||
          (normalizedAction === "schedule" &&
            normalizedNextVisit &&
            visitCareGapId === normalizedNextVisit);

        next[careGapId] = {
          careGapId,
          selected: shouldSelectByDefault,
          appointmentDate: config.requiresDate ? initialDate || "" : "",
        };
      }
      return next;
    },
    [normalizedAction, primaryCareGapId, nextVisitCareGapId, config.requiresDate]
  );

  const loadVisits = useCallback(async () => {
    if (!show || !patient?.id) return;

    setLoading(true);
    setLoadError(null);
    setFormError(null);
    setSubmitError(null);

    try {
      const url = buildVisitGapsUrl(
        patientId,
        actionConfig[normalizedAction].requestAction,
        primaryCareGapId
      );

      const response = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
      });

      const dueNowVisits = Array.isArray(response?.data?.dueNowVisits)
        ? response.data.dueNowVisits
        : [];
      const dueSoonVisits = Array.isArray(response?.data?.dueSoonVisits)
        ? response.data.dueSoonVisits
        : [];

      setVisitGroups({
        dueNowVisits,
        dueSoonVisits,
      });

      const selections = buildSelectionMap(dueSoonVisits, buildSelectionMap(dueNowVisits, {}));
      setVisitSelections(selections);
    } catch (error) {
      console.error("Failed to load visits for scheduling:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load visits. Please try again.";
      setLoadError(message);
      setVisitGroups({ dueNowVisits: [], dueSoonVisits: [] });
      setVisitSelections({});
    } finally {
      setLoading(false);
    }
  }, [
    show,
    patient,
    patientId,
    normalizedAction,
    primaryCareGapId,
    buildSelectionMap,
  ]);

  useEffect(() => {
    if (show) {
      loadVisits();
    } else {
      resetState();
    }
  }, [show, loadVisits, resetState]);

  const updateVisitSelection = useCallback((careGapId, updates) => {
    if (!careGapId) return;
    setVisitSelections((prev) => {
      const existing = prev[careGapId] || { careGapId, selected: false, appointmentDate: "" };
      return {
        ...prev,
        [careGapId]: {
          ...existing,
          ...updates,
        },
      };
    });
    if (updates.appointmentDate !== undefined) {
      setFormError(null);
    }
  }, []);

  const selectedVisits = useMemo(
    () => Object.values(visitSelections).filter((visit) => visit.selected),
    [visitSelections]
  );

  const handleSubmit = useCallback(async () => {
    if (submitting || loading) return;

    const validate = () => {
      if (!patientId) {
        return { ok: false, type: "submit", message: "Patient information is missing." };
      }

      if (!primaryCareGapId && !selectedVisits.length) {
        return { ok: false, type: "submit", message: "Unable to determine the visit to update." };
      }

      if (!selectedVisits.length) {
        return { ok: false, type: "form", message: "Select at least one visit." };
      }

      if (config.requiresDate) {
        const today = format(new Date(), "yyyy-MM-dd");
        for (const visit of selectedVisits) {
          if (!visit.appointmentDate) {
            return {
              ok: false,
              type: "form",
              message: "Select a date for each visit you want to schedule.",
            };
          }
          if (visit.appointmentDate < today) {
            return { ok: false, type: "form", message: "Selected dates must be today or later." };
          }
        }
      }

      return { ok: true };
    };

    const validation = validate();
    if (!validation.ok) {
      if (validation.type === "form") {
        setFormError(validation.message);
      } else {
        setSubmitError(validation.message);
      }
      return;
    }

    const payload = selectedVisits.map((visit) => ({
      careGapId: visit.careGapId,
      statusEnum: config.statusEnum,
      appointmentDate: config.requiresDate ? visit.appointmentDate : null,
    }));

    try {
      setSubmitting(true);
      setFormError(null);
      setSubmitError(null);

      const url = buildUpdateVisitGapsUrl(
        patientId,
        config.requestAction,
        selectedVisits[0]?.careGapId ?? primaryCareGapId
      );
      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_SCHEDULE_APPT_SUBMIT, {
        route: window?.location?.pathname,
        source_module: "scheduleAppointment.handleSubmit",
        action_type: normalizedAction,
        success: true
      });

      if (typeof onSuccess === "function") {
        await onSuccess();
      }

      if (typeof onHide === "function") {
        onHide();
      }
    } catch (error) {
      console.error("Failed to update appointment:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update the appointment. Please try again.";
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_SCHEDULE_APPT_SUBMIT, {
        route: window?.location?.pathname,
        source_module: "scheduleAppointment.handleSubmit",
        action_type: normalizedAction,
        success: false,
        error_code: getErrorCode(error, "quicklist_schedule_appt_failed")
      });
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, loading, selectedVisits, patientId, primaryCareGapId, config.requiresDate, config.statusEnum, config.requestAction, normalizedAction, onSuccess, onHide]);

  const renderVisitGroup = (label, visits, groupKey) => {
    if (!Array.isArray(visits) || !visits.length) {
      return null;
    }

    return (
      <Row className="mb-3" key={`${groupKey}-group`}>
        <Col>
          <span className="fw-bold d-block">{label}</span>
          <div className="mt-2 d-flex flex-column gap-2">
            {visits.map((visit, index) => {
              const careGapId = visit?.careGapId;
              if (!careGapId) return null;
              const postpartumPrefix = "Follow-Up after Child Birth";
              const visitDescription = visit?.description || visit?.gapDescription || "Scheduled visit";
              const isPostpartumVisit =
                (visit?.description || "").startsWith(postpartumPrefix) ||
                (visit?.gapDescription || "").startsWith(postpartumPrefix);
              const selection = visitSelections[careGapId] || {
                careGapId,
                selected: false,
                appointmentDate: "",
              };
              const checkboxId = `${componentName}-${groupKey}-${careGapId}-${index}`;
              const visitDateDisplay = formatDate(resolveVisitAppointmentDate(visit));
              const { text: dueText, icon: dueIcon } = formatDueDate(visit?.nextVisitDays);
              const normalizedDueText = dueText ? dueText.trim().toLowerCase() : "";
              const showDueBadge =
                !!normalizedDueText &&
                normalizedDueText !== "today" &&
                !normalizedDueText.endsWith("ago");
              const statusBadgeText = getStatusBadgeText(visit);

              return (
                <div key={checkboxId} className="d-flex flex-column">
                  <div className="d-flex flex-row justify-content-between align-items-start">
                    <div className="form-check">
                      <input
                        id={checkboxId}
                        className="form-check-input"
                        type="checkbox"
                        name={checkboxId}
                        checked={!!selection.selected}
                        onChange={(event) =>
                          updateVisitSelection(careGapId, { selected: event.target.checked })
                        }
                      />
                      <div className="d-flex flex-column ms-2">
                        <label className="form-check-label" htmlFor={checkboxId}>
                          {isPostpartumVisit ? (
                            <OverlayTrigger
                              placement="auto"
                              overlay={(
                                <Tooltip id={`${componentName}-postpartumTooltip-${careGapId}`}>
                                  Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
                                </Tooltip>
                              )}
                            >
                              <span>{visitDescription}</span>
                            </OverlayTrigger>
                          ) : (
                            visitDescription
                          )}
                        </label>
                        <div className="d-flex flex-row align-items-center gap-2 mt-1">
                          {visitDateDisplay && (
                            <Badge pill bg="transparent" className="border border-secondary text-dark">
                              Scheduled for {visitDateDisplay}
                            </Badge>
                          )}
                          {showDueBadge && (
                            <Badge pill bg="transparent" className="border border-secondary text-dark d-inline-flex align-items-center">
                              <i className={`fa-regular ${dueIcon || "fa-calendar"} me-1`}></i>
                              {dueText}
                            </Badge>
                          )}
                          {statusBadgeText && (
                            <Badge pill bg="transparent" className="border border-danger text-danger">
                              {statusBadgeText}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {config.requiresDate && (
                      <div className="ms-3">
                        <EQHDatePicker
                          id={`${componentName}-datepicker-${careGapId}`}
                          className="form-control"
                          value={selection.appointmentDate || ""}
                          onChange={(value) =>
                            updateVisitSelection(careGapId, {
                              appointmentDate: value?.strDate || "",
                              selected: true,
                            })
                          }
                          onInputClick={() => setFormError(null)}
                          min={format(new Date(), "yyyy-MM-dd")}
                          max="9999-12-31"
                          disabled={!selection.selected}
                          style={{ maxWidth: "fit-content" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Col>
      </Row>
    );
  };

  const hasVisits = (visitGroups?.dueNowVisits?.length ?? 0) > 0 || (visitGroups?.dueSoonVisits?.length ?? 0) > 0;

  const modalBody = (
    <div>
      <Row className="mb-3">
        <Col>
          <span id={`${componentName}-patientNameLabel`} className="fw-bold d-block">
            Patient Name
          </span>
          <span id={`${componentName}-patientNameValue`} className="forced-capital d-block">{patient?.fullName || ""}</span>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <p>{config.description}</p>
        </Col>
      </Row>

      {loadError && (
        <Row className="mb-3">
          <Col>
            <div className="text-danger">{loadError}</div>
          </Col>
        </Row>
      )}

      {formError && (
        <Row className="mb-3">
          <Col>
            <div className="text-danger">{formError}</div>
          </Col>
        </Row>
      )}

      {submitError && (
        <Row className="mb-3">
          <Col>
            <div className="text-danger">{submitError}</div>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {renderVisitGroup("Recommended Now", visitGroups.dueNowVisits, "now")}
          {renderVisitGroup("Recommended Soon", visitGroups.dueSoonVisits, "soon")}
          {!visitGroups.dueNowVisits.length && !visitGroups.dueSoonVisits.length && (
            <div className="text-muted fst-italic small">No visits found.</div>
          )}
        </>
      )}

      {submitting && (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Updating appointment...</span>
        </div>
      )}
    </div>
  );

  return (
    <Modals
      centered
      show={show}
      onHide={onHide}
      size="lg"
      title={config.title}
      titleid={`${componentName}-${config.requestAction}Modal`}
      body={modalBody}
      completeBtn="Submit"
      completeBtnId={`${componentName}-${config.requestAction}Submit`}
      disabledClickButton={!hasVisits}
      closeBtn="Cancel"
      closeBtnId={`${componentName}-${config.requestAction}Cancel`}
      onCreate={handleSubmit}
    />
  );
};

ScheduledAppointment.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  patient: PropTypes.object,
  appointmentAction: PropTypes.oneOf(["schedule", "reschedule", "unschedule"]),
  onSuccess: PropTypes.func,
};

ScheduledAppointment.defaultProps = {
  show: false,
  onHide: undefined,
  patient: {},
  appointmentAction: "schedule",
  onSuccess: undefined,
};

export default ScheduledAppointment;
